import { extraCellNodeId, type ValidationCode, type ValidationIssue } from '@record-fill';
import type { GeneratedRecordIssue } from '@/entities/GeneratedRecord';

// The server names the stage problem after the payload key, which has no field on the sheet.
export const STAGE_ISSUE_FIELD_ID = 'stageId';

// A Record keyed by the union, so a code added to the engine without being listed here fails to
// compile instead of being silently mapped to 'type'.
const ENGINE_CODES: Record<ValidationCode, true> = {
  type: true,
  required: true,
  min: true,
  max: true,
  minLength: true,
  maxLength: true,
  regex: true,
  option: true,
  anchor: true,
  frozen: true,
  forbidden: true,
};

function isValidationCode(code: string): code is ValidationCode {
  return Object.hasOwn(ENGINE_CODES, code);
}

// The server addresses an added-row cell by position; the sheet addresses its control by the
// display-only node id, so the issue is re-keyed here and never by an id inside the snapshot.
export function toSheetIssue(issue: GeneratedRecordIssue): ValidationIssue {
  const cell = issue.cell;
  const fieldId =
    cell && cell.columnIndex !== undefined
      ? extraCellNodeId(cell.tableId, cell.rowIndex, cell.columnIndex)
      : issue.fieldId;

  return {
    fieldId,
    code: isValidationCode(issue.code) ? issue.code : 'type',
    message: issue.message,
    ...(cell === undefined ? {} : { cell }),
  };
}

// Removing a new row renumbers the rows below it. A cell issue moves up one row with its node id,
// or it would point at the row that took its number. A row issue goes away: it can be about the
// row's position, as the new-rows limit is, and the removal changes that.
export function issuesAfterRowRemoval(issues: ValidationIssue[], tableId: string, rowIndex: number): ValidationIssue[] {
  return issues.flatMap((issue) => {
    const cell = issue.cell;
    if (cell?.tableId !== tableId || cell.rowIndex < rowIndex) return [issue];
    if (cell.rowIndex === rowIndex || cell.columnIndex === undefined) return [];
    const shifted = { ...cell, rowIndex: cell.rowIndex - 1 };
    return [{ ...issue, fieldId: extraCellNodeId(tableId, shifted.rowIndex, cell.columnIndex), cell: shifted }];
  });
}

export function dedupeIssues(issues: ValidationIssue[]): ValidationIssue[] {
  const seen = new Set<string>();
  return issues.filter((issue) => {
    // Two added rows can fail the same column with the same code; the position keeps both.
    const key = `${issue.fieldId}:${issue.code}:${issue.cell?.rowIndex ?? ''}:${issue.cell?.columnIndex ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export interface SplitServerIssues {
  fieldIssues: ValidationIssue[];
  stageMessage: string | null;
}

export function splitServerIssues(issues: GeneratedRecordIssue[]): SplitServerIssues {
  const stageIssue = issues.find((issue) => issue.fieldId === STAGE_ISSUE_FIELD_ID);
  return {
    fieldIssues: issues.filter((issue) => issue.fieldId !== STAGE_ISSUE_FIELD_ID).map(toSheetIssue),
    stageMessage: stageIssue?.message ?? null,
  };
}
